import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { groupSlotsByPeriod } from '../utils/scheduleSlotFinder';

const SlotCard = ({ slot, onSelect }) => (
  <Box
    onClick={() => onSelect(slot)}
    sx={{
      p: 1.5,
      border: '1px solid #e5e7eb',
      borderRadius: 1,
      cursor: 'pointer',
      bgcolor: '#fff',
      transition: 'all 0.15s',
      '&:hover': {
        backgroundColor: 'rgba(228, 201, 132, 0.15)',
        borderColor: '#E4C984',
        boxShadow: 1,
      },
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 700, color: '#171717' }}>
      {slot.startTimeFormatted} – {slot.endTimeFormatted}
    </Typography>
    {slot.therapistLabels?.length > 0 && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
        {slot.therapistLabels.join(' · ')}
      </Typography>
    )}
    {slot.room && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {slot.room}
      </Typography>
    )}
  </Box>
);

const PeriodColumn = ({ title, subtitle, emoji, headerBg, borderColor, slots, onSelect }) => (
  <Box
    sx={{
      border: '1px solid #e5e7eb',
      borderRadius: 1,
      overflow: 'hidden',
      bgcolor: '#fafafa',
    }}
  >
    <Box sx={{ px: 2, py: 1, bgcolor: headerBg, borderBottom: `1px solid ${borderColor}` }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {emoji} {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
    </Box>
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 80 }}>
      {slots.length > 0 ? (
        slots.map((slot, index) => (
          <SlotCard key={`${slot.startMinutes}-${index}`} slot={slot} onSelect={onSelect} />
        ))
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No {title.toLowerCase()} slots
        </Typography>
      )}
    </Box>
  </Box>
);

const ScheduleSlotPickerModal = ({
  open,
  onClose,
  onSelect,
  treatmentName,
  date,
  loading = false,
  slots = [],
  failureHints = [],
}) => {
  const byPeriod = useMemo(() => groupSlotsByPeriod(slots), [slots]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Choose available slot
        {(treatmentName || date) && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {[treatmentName, date].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={28} />
            <Typography sx={{ ml: 2 }}>Searching for available slots...</Typography>
          </Box>
        ) : slots.length > 0 ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Found {slots.length} option{slots.length !== 1 ? 's' : ''}. Click a slot to apply time, therapist, and room.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
                alignItems: 'start',
              }}
            >
              <PeriodColumn
                title="Morning"
                subtitle="Before 2:00 PM"
                emoji="☀️"
                headerBg="#fffbeb"
                borderColor="#fde68a"
                slots={byPeriod.morning}
                onSelect={onSelect}
              />
              <PeriodColumn
                title="Afternoon"
                subtitle="From 2:00 PM"
                emoji="🌤️"
                headerBg="#fff7ed"
                borderColor="#fed7aa"
                slots={byPeriod.afternoon}
                onSelect={onSelect}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No available slots found for this date and treatment.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try another date, or enter a custom time below and use Check Availability.
            </Typography>
            {failureHints.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  textAlign: 'left',
                  bgcolor: 'rgba(245, 158, 11, 0.08)',
                  border: '1px solid rgba(245, 158, 11, 0.35)',
                  borderRadius: 1,
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  Scheduler notes:
                </Typography>
                {failureHints.map((hint, i) => (
                  <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {hint}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleSlotPickerModal;
